import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  Truck, SignOut, Buildings, Users, Car,
  GearSix, ChartLine, WhatsappLogo, List, Package
} from '@phosphor-icons/react';

export const Layout = ({ children }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(true);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const menuItems = [
    { name: 'Dashboard', path: '/dashboard', icon: ChartLine, roles: ['super_admin', 'empresa_admin', 'logistica', 'sap_user'] },
    { name: 'Viajes', path: '/shipments', icon: Package, roles: ['super_admin', 'empresa_admin', 'logistica'] },
    { name: 'Empresas', path: '/companies', icon: Buildings, roles: ['super_admin'] },
    { name: 'Conductores', path: '/drivers', icon: Users, roles: ['super_admin', 'empresa_admin', 'logistica'] },
    { name: 'Vehículos', path: '/vehicles', icon: Car, roles: ['super_admin', 'empresa_admin', 'logistica'] },
    { name: 'Configuración', path: '/integration-config', icon: GearSix, roles: ['super_admin', 'empresa_admin'] },
    { name: 'WhatsApp Emulator', path: '/whatsapp-emulator', icon: WhatsappLogo, roles: ['super_admin', 'empresa_admin', 'logistica'] },
  ];

  const filteredMenuItems = menuItems.filter(item =>
    item.roles.includes(user?.rol)
  );

  return (
    <div className="flex h-screen bg-neutral-50" data-testid="app-layout">
      {/* Sidebar */}
      <aside
        className={`${sidebarOpen ? 'w-64' : 'w-20'} bg-white border-r border-neutral-200 transition-[width] duration-300 flex flex-col`}
        data-testid="sidebar"
      >
        {/* Logo */}
        <div className="h-16 flex items-center px-6 border-b border-neutral-200">
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="mr-3 p-1 hover:bg-neutral-100 rounded"
            data-testid="toggle-sidebar-btn"
          >
            <List size={22} />
          </button>
          <Truck size={28} weight="bold" className="text-blue-700" />
          {sidebarOpen && (
            <span className="ml-2 text-xl font-bold tracking-tight text-neutral-900">
              SRoute
            </span>
          )}
        </div>

        {/* Navigation */}
        <nav className="flex-1 px-3 py-6 space-y-1 overflow-y-auto">
          {filteredMenuItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            return (
              <Link
                key={item.path}
                to={item.path}
                data-testid={`nav-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
                className={`flex items-center px-4 py-3 rounded-md transition-colors ${
                  isActive
                    ? 'bg-blue-50 text-blue-700 font-medium border-l-2 border-blue-700'
                    : 'text-neutral-700 hover:bg-neutral-100'
                }`}
              >
                <Icon size={22} weight={isActive ? 'fill' : 'regular'} />
                {sidebarOpen && <span className="ml-3 text-sm">{item.name}</span>}
              </Link>
            );
          })}
        </nav>

        {/* User Info */}
        <div className="border-t border-neutral-200 p-4">
          <div className="flex items-center">
            <div className="w-10 h-10 rounded-full bg-blue-700 flex items-center justify-center text-white font-bold text-sm shrink-0">
              {user?.nombre?.charAt(0)?.toUpperCase()}
            </div>
            {sidebarOpen && (
              <div className="ml-3 flex-1 min-w-0">
                <p className="text-sm font-medium text-neutral-900 truncate">
                  {user?.nombre}
                </p>
                <p className="text-xs text-neutral-500 truncate uppercase tracking-widest">
                  {user?.rol?.replace('_', ' ')}
                </p>
              </div>
            )}
          </div>
          {sidebarOpen && (
            <button
              onClick={handleLogout}
              data-testid="logout-button"
              className="mt-4 w-full flex items-center justify-center px-4 py-2 bg-neutral-100 hover:bg-neutral-200 rounded-md text-sm font-medium text-neutral-700 transition-colors"
            >
              <SignOut size={18} className="mr-2" />
              Cerrar Sesión
            </button>
          )}
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 overflow-auto" data-testid="main-content">
        <div className="p-6 md:p-8 max-w-7xl mx-auto">
          {children}
        </div>
      </main>
    </div>
  );
};
